import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, query, limit, onSnapshot } from 'firebase/firestore';
import { Plus, X, MessageCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const EMOJI_OPTIONS = ['💭', '☕', '🔥', '🎧', '⚡', '🌙', '🍕', '🎈', '💻', '🚀'];

const NotesCarousel = ({ navigate }) => {
    const { currentUser, userProfile, updateNote } = useAuth();
    const [notesList, setNotesList] = useState([]);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedEmoji, setSelectedEmoji] = useState('💭');
    const [noteText, setNoteText] = useState('');
    const [selectedUserNote, setSelectedUserNote] = useState(null);

    // Fetch active notes from users
    useEffect(() => {
        const q = query(collection(db, 'users'), limit(30));
        const unsub = onSnapshot(q, (snapshot) => {
            const usersWithNotes = [];
            const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                if (data.currentNote && data.currentNote.updatedAt > twentyFourHoursAgo) {
                    usersWithNotes.push({
                        id: docSnap.id,
                        displayName: data.displayName || 'User',
                        username: data.username || 'user',
                        photoURL: data.photoURL,
                        note: data.currentNote
                    });
                }
            });
            setNotesList(usersWithNotes);
        }, (err) => console.error("Notes fetch error:", err));

        return () => unsub();
    }, []);

    const myNote = userProfile?.currentNote;
    const isMyNoteActive = myNote && (Date.now() - myNote.updatedAt < 24 * 60 * 60 * 1000);

    const handleSaveNote = async (e) => {
        if (e) e.preventDefault();
        const textToSave = noteText.trim();
        if (!textToSave) return;
        try {
            await updateNote(textToSave, selectedEmoji);
            setIsNoteModalOpen(false);
            setNoteText('');
        } catch (err) {
            console.error("Save note error:", err);
            toast.error("Failed to share thought");
        }
    };

    const handleClearNote = async () => {
        try {
            await updateNote(null);
            setIsNoteModalOpen(false);
            setNoteText('');
        } catch (err) {
            console.error("Clear note error:", err);
        }
    };

    const handleNoteClick = (item) => {
        if (item.id === currentUser?.uid) {
            setNoteText(myNote?.text || '');
            setSelectedEmoji(myNote?.emoji || '💭');
            setIsNoteModalOpen(true);
        } else {
            setSelectedUserNote(item);
        }
    };

    const handleReplyToNote = () => {
        if (!selectedUserNote) return;
        const chatId = [currentUser.uid, selectedUserNote.id].sort().join('_');
        navigate('chat', { chatId, initialPrompt: `Replying to note "${selectedUserNote.note.emoji} ${selectedUserNote.note.text}"` });
        setSelectedUserNote(null);
    };

    return (
        <div className="w-full bg-white dark:bg-[#111827]/80 backdrop-blur-md border border-slate-200/80 dark:border-slate-800/80 rounded-2xl sm:rounded-3xl p-3 sm:p-4 shadow-sm overflow-hidden">
            {/* Horizontal Scroll Track */}
            <div className="flex items-start gap-3 sm:gap-4 overflow-x-auto no-scrollbar py-1 px-0.5">
                {/* 1. Current User Note */}
                <div 
                    onClick={() => {
                        setNoteText(isMyNoteActive ? myNote.text : '');
                        setSelectedEmoji(isMyNoteActive ? myNote.emoji : '💭');
                        setIsNoteModalOpen(true);
                    }}
                    className="flex flex-col items-center flex-shrink-0 cursor-pointer group w-16 sm:w-20 relative pt-7 select-none"
                >
                    {/* Floating Thought Bubble */}
                    {isMyNoteActive ? (
                        <div className="absolute top-0 max-w-[76px] sm:max-w-[85px] px-2 py-0.5 sm:py-1 bg-gradient-to-r from-sky-500 to-indigo-600 text-white rounded-xl sm:rounded-2xl text-[10px] sm:text-[11px] font-medium shadow-sm truncate animate-fade-in text-center">
                            <span className="mr-0.5">{myNote.emoji}</span>
                            <span>{myNote.text}</span>
                        </div>
                    ) : (
                        <div className="absolute top-0 px-2 py-0.5 bg-slate-100 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 rounded-full text-[10px] font-semibold border border-slate-200/80 dark:border-slate-700/80 shadow-xs">
                            + Note
                        </div>
                    )}

                    {/* Avatar */}
                    <div className="relative w-12 h-12 sm:w-14 sm:h-14 min-w-[48px] min-h-[48px] sm:min-w-[56px] sm:min-h-[56px] rounded-full p-[2px] bg-gradient-to-tr from-sky-400 to-indigo-500 group-hover:scale-105 active:scale-95 transition-transform duration-150 shadow-xs flex-shrink-0">
                        <img
                            src={userProfile?.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${currentUser?.uid}`}
                            alt="Your avatar"
                            className="w-full h-full rounded-full object-cover aspect-square bg-slate-100 dark:bg-slate-800 pointer-events-none"
                        />
                        {!isMyNoteActive && (
                            <div className="absolute bottom-0 right-0 w-4 h-4 sm:w-4.5 sm:h-4.5 bg-sky-500 rounded-full border-2 border-white dark:border-[#111827] flex items-center justify-center text-white">
                                <Plus className="w-2.5 h-2.5 stroke-[3]" />
                            </div>
                        )}
                    </div>
                    <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 mt-1.5 truncate w-full text-center">
                        You
                    </span>
                </div>

                {/* 2. Other Users' Notes */}
                {notesList
                    .filter(item => item.id !== currentUser?.uid)
                    .map(item => (
                        <div
                            key={item.id}
                            onClick={() => handleNoteClick(item)}
                            className="flex flex-col items-center flex-shrink-0 cursor-pointer group w-16 sm:w-20 relative pt-7 select-none"
                        >
                            {/* Floating Thought Bubble */}
                            <div className="absolute top-0 max-w-[76px] sm:max-w-[85px] px-2 py-0.5 sm:py-1 bg-white dark:bg-[#1e293b] border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-xl sm:rounded-2xl text-[10px] sm:text-[11px] font-medium shadow-xs truncate group-hover:border-sky-500/50 transition-colors animate-fade-in text-center">
                                <span className="mr-0.5">{item.note.emoji}</span>
                                <span>{item.note.text}</span>
                            </div>

                            {/* Avatar with gradient ring */}
                            <div className="w-12 h-12 sm:w-14 sm:h-14 min-w-[48px] min-h-[48px] sm:min-w-[56px] sm:min-h-[56px] rounded-full p-[2px] bg-gradient-to-tr from-amber-400 via-rose-500 to-indigo-600 group-hover:scale-105 active:scale-95 transition-transform duration-150 shadow-xs flex-shrink-0">
                                <img
                                    src={item.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${item.id}`}
                                    alt={item.displayName}
                                    className="w-full h-full rounded-full object-cover aspect-square bg-slate-100 dark:bg-slate-800 pointer-events-none"
                                />
                            </div>
                            <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 mt-1.5 truncate w-full text-center">
                                {item.displayName.split(' ')[0]}
                            </span>
                        </div>
                    ))}
            </div>

            {/* Modal: Set / Edit My Note (Teleported to document.body via Portal) */}
            {isNoteModalOpen && typeof document !== 'undefined' && createPortal(
                <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl max-w-sm w-full p-5 sm:p-6 shadow-2xl animate-scale-in space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-bold text-slate-900 dark:text-white">Share a Thought</h3>
                            <button
                                onClick={() => setIsNoteModalOpen(false)}
                                className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                            Notes disappear after 24 hours. Friends will see your thought floating above your avatar.
                        </p>

                        <form onSubmit={handleSaveNote} className="space-y-4">
                            {/* Emoji Selector */}
                            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1">
                                {EMOJI_OPTIONS.map(emoji => (
                                    <button
                                        type="button"
                                        key={emoji}
                                        onClick={() => setSelectedEmoji(emoji)}
                                        className={`w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 text-base sm:text-lg rounded-xl flex items-center justify-center transition-all ${
                                            selectedEmoji === emoji ? 'bg-sky-500/20 scale-110 ring-2 ring-sky-400' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                                        }`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>

                            {/* Text Input */}
                            <div className="relative">
                                <span className="absolute left-3.5 top-3 text-base">{selectedEmoji}</span>
                                <input
                                    type="text"
                                    maxLength="60"
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="What's on your mind? (60 chars max)"
                                    className="w-full pl-10 pr-12 py-3 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 rounded-2xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                                />
                                <span className="absolute right-3.5 top-3.5 text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                                    {60 - noteText.length}
                                </span>
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                {isMyNoteActive ? (
                                    <button
                                        type="button"
                                        onClick={handleClearNote}
                                        className="text-xs text-rose-500 hover:text-rose-600 font-semibold"
                                    >
                                        Delete Note
                                    </button>
                                ) : <div />}
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsNoteModalOpen(false)}
                                        className="px-4 py-2 text-xs font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 rounded-xl"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!noteText.trim()}
                                        className="px-5 py-2 text-xs font-semibold bg-sky-500 hover:bg-sky-600 text-white rounded-xl shadow-xs transition-all disabled:opacity-50"
                                    >
                                        Share
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal: View & Reply to User's Note (Teleported to document.body via Portal) */}
            {selectedUserNote && typeof document !== 'undefined' && createPortal(
                <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl max-w-sm w-full p-6 shadow-2xl animate-scale-in space-y-4 text-center">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setSelectedUserNote(null)}
                                className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="w-16 h-16 mx-auto rounded-full p-[2px] bg-gradient-to-tr from-sky-400 to-indigo-600">
                            <img
                                src={selectedUserNote.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${selectedUserNote.id}`}
                                alt=""
                                className="w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                            />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 dark:text-white text-sm sm:text-base">{selectedUserNote.displayName}</h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400">@{selectedUserNote.username}</p>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800">

                            <span className="text-2xl block mb-1">{selectedUserNote.note.emoji}</span>
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{selectedUserNote.note.text}</p>
                        </div>
                        <button
                            onClick={handleReplyToNote}
                            className="w-full py-3 bg-sky-500 hover:bg-sky-600 text-white font-semibold text-xs sm:text-sm rounded-2xl shadow-xs flex items-center justify-center gap-2 transition-all active:scale-98"
                        >
                            <MessageCircle className="w-4 h-4" />
                            <span>Reply in Direct Message</span>
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default NotesCarousel;
